import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface Principle {
  title: string;
  text: string;
}

interface Subsection {
  title: string;
  text?: string;
  items?: string[];
}

interface Phase {
  num: string;
  name: string;
  objective: string;
  activities?: string[];
  subsections?: Subsection[];
  deliverable: string;
  closeCriteria: string;
}

interface DocRow {
  doc: string;
  phase: string;
  approvedBy: string;
}

interface CadenceRow {
  label: string;
  value: string;
}

interface Translations {
  breadcrumb_home: string;
  current: string;
  label: string;
  title: string;
  lead: string;
  intro: string;
  define_title: string;
  phases_title: string;
  phases_intro: string;
  activities_label: string;
  deliverable_label: string;
  close_label: string;
  docs_title: string;
  docs_intro: string;
  doc_col: string;
  phase_col: string;
  approved_col: string;
  cadence_title: string;
  cta_label: string;
  cta_title: string;
  cta_btn: string;
  nav_quienes: string;
  nav_servicios: string;
  nav_trabajos: string;
  nav_contacto: string;
  nav_metodologia: string;
  footer_sub: string;
  footer_nav: string;
  footer_services: string;
  footer_rights: string;
}

@Component({
  selector: 'app-metodologia',
  templateUrl: './metodologia.component.html',
  styleUrls: ['./metodologia.component.scss'],
})
export class MetodologiaComponent implements OnInit {

  lang: 'ESP' | 'ENG' = 'ESP';
  t: Translations = {} as Translations;

  // ─── Traducciones de UI ──────────────────────────────────────────────────────
  private translations: Record<'ESP' | 'ENG', Translations> = {
    ESP: {
      breadcrumb_home: 'Inicio',
      current:         'Cómo trabajamos',
      label:           'Metodología',
      title:           'Software que funciona,<br><em>entregado con confianza</em>',
      lead:            'En Patagonia Chain entregamos software que funciona — y la certeza de que el camino para llegar ahí estuvo en buenas manos.',
      intro:           'Nuestra metodología es el resultado de años trabajando junto a empresas reales, con desafíos reales. Aprendimos que los proyectos exitosos comparten algo en común: no es el tamaño del equipo ni la complejidad de la tecnología. Es la claridad con la que se trabaja desde el primer día. Este documento describe nuestra forma de trabajar: las fases de cada proyecto, los entregables que generamos en cada etapa y los criterios que usamos para avanzar con confianza. Es el marco que nos permite ser predecibles, transparentes y consistentes — independientemente del proyecto o del equipo que lo ejecute.',
      define_title:    'Lo que nos define',
      phases_title:    'Fases del proyecto',
      phases_intro:    'Todo proyecto de desarrollo en Patagonia Chain atraviesa cinco fases. Cada una tiene un propósito específico, entregables claros y un criterio de cierre acordado entre ambas partes antes de avanzar a la siguiente.',
      activities_label: 'Actividades',
      deliverable_label: 'Entregable',
      close_label:     'Criterio de cierre',
      docs_title:      'Documentos del proyecto',
      docs_intro:      'Cada fase genera documentos específicos que garantizan trazabilidad, alineación y protección para ambas partes. La conformidad sobre cada documento habilita el avance a la siguiente etapa.',
      doc_col:         'Documento',
      phase_col:       'Fase',
      approved_col:    'Aprobado por',
      cadence_title:   'Comunicación y cadencia',
      cta_label:       '¿Tenés un proyecto?',
      cta_title:       'Hablemos sobre<br><em>tu proyecto</em>',
      cta_btn:         'Escribinos',
      nav_quienes:     'Quiénes somos',
      nav_servicios:   'Servicios',
      nav_trabajos:    'Trabajos',
      nav_contacto:    'Contacto',
      nav_metodologia: 'Cómo trabajamos',
      footer_sub:      'Desarrollo de Software',
      footer_nav:      'Navegación',
      footer_services: 'Servicios',
      footer_rights:   'Todos los derechos reservados.',
    },
    ENG: {
      breadcrumb_home: 'Home',
      current:         'How we work',
      label:           'Methodology',
      title:           'Software that works,<br><em>delivered with confidence</em>',
      lead:            'At Patagonia Chain we deliver software that works and the certainty that the path to get there was in good hands.',
      intro:           'Our methodology is the result of years working alongside real companies, with real challenges. We learned that successful projects share something in common: it\'s not the size of the team or the complexity of the technology. It\'s the clarity with which we work from day one. This document describes how we work: the phases of each project, the deliverables we generate at each stage, and the criteria we use to move forward with confidence. It\'s the framework that lets us be predictable, transparent, and consistent — regardless of the project or the team executing it.',
      define_title:    'What defines us',
      phases_title:    'Project phases',
      phases_intro:    'Every development project at Patagonia Chain goes through five phases. Each one has a specific purpose, clear deliverables, and a closing criterion agreed upon by both parties before moving to the next.',
      activities_label: 'Activities',
      deliverable_label: 'Deliverable',
      close_label:     'Closing criterion',
      docs_title:      'Project documents',
      docs_intro:      'Each phase generates specific documents that guarantee traceability, alignment, and protection for both parties. Agreement on each document enables progress to the next stage.',
      doc_col:         'Document',
      phase_col:       'Phase',
      approved_col:    'Approved by',
      cadence_title:   'Communication & cadence',
      cta_label:       'Have a project in mind?',
      cta_title:       "Let's talk about<br><em>your project</em>",
      cta_btn:         'Get in touch',
      nav_quienes:     'About us',
      nav_servicios:   'Services',
      nav_trabajos:    'Works',
      nav_contacto:    'Contact',
      nav_metodologia: 'How we work',
      footer_sub:      'Software Development',
      footer_nav:      'Navigation',
      footer_services: 'Services',
      footer_rights:   'All rights reserved.',
    },
  };

  // ─── "Lo que nos define" ─────────────────────────────────────────────────────
  private principlesData: Record<'ESP' | 'ENG', Principle[]> = {
    ESP: [
      {
        title: 'Entendemos el negocio antes de proponer una solución.',
        text: 'Cada proyecto empieza con escucha activa. Antes de diseñar cualquier cosa, nos tomamos el tiempo de entender el contexto, los procesos y los objetivos reales del cliente. Eso es lo que nos permite construir algo que genera valor genuino, no solo algo que técnicamente funciona.',
      },
      {
        title: 'Trabajamos con foco y con método.',
        text: 'Cada fase tiene un objetivo claro, entregables definidos y criterios de aceptación acordados entre las partes. El cliente sabe en todo momento qué se está construyendo, qué se entregó y qué viene después. No hay cajas negras.',
      },
      {
        title: 'La comunicación es el motor del proyecto.',
        text: 'Creemos que un proyecto bien ejecutado es también un proyecto bien comunicado. Mantenemos canales abiertos, acordamos la cadencia de avance desde el inicio y documentamos las decisiones importantes. La confianza se construye con transparencia sostenida en el tiempo, no con una buena primera reunión.',
      },
      {
        title: 'Construimos para que dure.',
        text: 'Diseñamos pensando en el día después del lanzamiento. Documentamos, transferimos conocimiento y nos aseguramos de que el cliente pueda operar con autonomía cuando el proyecto cierra.',
      },
      {
        title: 'El éxito lo medimos en impacto, no en entrega.',
        text: 'Un proyecto terminado que no cambia nada no es un proyecto exitoso. Nos importa que lo que construimos tenga un efecto real y medible en el negocio del cliente.',
      },
    ],
    ENG: [
      {
        title: 'We understand the business before proposing a solution.',
        text: 'Every project starts with active listening. Before designing anything, we take the time to understand the client\'s context, processes, and real objectives. That\'s what lets us build something that generates genuine value, not just something that technically works.',
      },
      {
        title: 'We work with focus and method.',
        text: 'Every phase has a clear objective, defined deliverables, and acceptance criteria agreed upon by both parties. The client always knows what\'s being built, what\'s been delivered, and what comes next. No black boxes.',
      },
      {
        title: 'Communication is the engine of the project.',
        text: 'We believe a well-executed project is also a well-communicated one. We keep channels open, agree on a progress cadence from the start, and document important decisions. Trust is built through sustained transparency over time, not a good first meeting.',
      },
      {
        title: 'We build things to last.',
        text: 'We design with the day after launch in mind. We document, transfer knowledge, and make sure the client can operate autonomously once the project closes.',
      },
      {
        title: 'We measure success in impact, not delivery.',
        text: 'A finished project that changes nothing is not a successful project. What matters to us is that what we build has a real, measurable effect on the client\'s business.',
      },
    ],
  };

  // ─── Fases del proyecto ───────────────────────────────────────────────────────
  private phasesData: Record<'ESP' | 'ENG', Phase[]> = {
    ESP: [
      {
        num: '01',
        name: 'Inicio',
        objective: 'Alinear a todos los involucrados sobre qué se va a construir, cómo se va a trabajar y cuáles son las condiciones de éxito del proyecto.',
        activities: [
          'Reunión de Kickoff con todos los stakeholders',
          'Definición y firma del Acta de Kickoff',
          'Identificación de ambientes requeridos, responsables y fechas de disponibilidad',
          'Establecimiento del canal de comunicación y frecuencia de reuniones de avance',
        ],
        deliverable: 'Acta de Kickoff firmada por ambas partes.',
        closeCriteria: 'La conformidad de ambas partes sobre el Acta de Kickoff habilita el inicio formal del proyecto.',
      },
      {
        num: '02',
        name: 'Relevamiento',
        objective: 'Entender en profundidad el estado actual del negocio y los procesos que el proyecto va a impactar.',
        activities: [
          'Entrevistas con usuarios clave y referentes funcionales del cliente',
          'Mapeo de procesos actuales',
          'Identificación de oportunidades de mejora y gaps',
          'Relevamiento de sistemas existentes, fuentes de datos e integraciones',
        ],
        subsections: [
          {
            title: 'Documento AS IS',
            text: 'Describe el estado actual del negocio, los procesos relevados y los problemas identificados. Es la base sobre la que se construye la solución.',
          },
        ],
        deliverable: 'Documento AS IS aprobado por el referente funcional del cliente.',
        closeCriteria: 'La aprobación del Documento AS IS por parte del referente funcional del cliente confirma que el relevamiento refleja fielmente la realidad del negocio y habilita el avance hacia la fase de diseño. En proyectos greenfield, esta fase se orienta a documentar el contexto y los objetivos que guiarán la solución.',
      },
      {
        num: '03',
        name: 'Diseño',
        objective: 'Definir con precisión qué se va a construir y cómo se va a construir, antes de escribir una línea de código. Esta fase produce dos documentos complementarios con audiencias distintas.',
        subsections: [
          {
            title: 'Especificación Funcional — FSD',
            text: 'Describe qué hace el sistema en lenguaje de negocio. Lo puede leer y aprobar el cliente sin conocimiento técnico. Incluye: resumen ejecutivo, actores del sistema, flujos funcionales por módulo, reglas de negocio, componentes incluidos con descripción detallada, criterios de aceptación por funcionalidad y exclusiones explícitas del alcance.',
          },
          {
            title: 'Documento de Diseño Técnico — TDD',
            text: 'Describe cómo se implementa la solución. Audiencia técnica. Incluye: arquitectura de la solución, stack tecnológico con justificación, modelo de datos, integraciones con sistemas externos, estrategia de branching, pipeline CI/CD, consideraciones de seguridad y performance, y decisiones técnicas con sus trade-offs.',
          },
        ],
        deliverable: 'FSD aprobado por el referente funcional del cliente. TDD aprobado por el equipo técnico.',
        closeCriteria: 'La aprobación del FSD por parte del cliente es el punto de partida del desarrollo. Representa el acuerdo mutuo sobre qué se va a construir y es la referencia que guía al equipo técnico durante toda la implementación.',
      },
      {
        num: '04',
        name: 'Implementación',
        objective: 'Construir la solución definida en el FSD y el TDD, con visibilidad permanente para el cliente.',
        activities: [
          'Desarrollo iterativo en sprints de dos semanas por defecto, ajustable según el proyecto',
          'Reuniones de avance periódicas con el cliente (semanales o quincenales)',
          'Demo al cliente al cierre de cada sprint',
          'Testing interno continuo',
        ],
        deliverable: 'Acta de Reunión de Avance por cada reunión periódica. Acta de Aceptación de Entregable por cada hito completado y validado.',
        closeCriteria: 'Cada hito completado cuenta con su correspondiente Acta de Aceptación aprobada, lo que confirma que la solución construida cumple con lo acordado en el FSD y habilita el avance hacia el cierre del proyecto.',
      },
      {
        num: '05',
        name: 'Cierre y Go-Live',
        objective: 'Validar que la solución cumple lo acordado, ponerla en producción de forma controlada y transferir el conocimiento al cliente.',
        subsections: [
          {
            title: 'Testing y UAT',
            items: [
              'Ejecución del Plan de Pruebas sobre el entorno de staging',
              'UAT con usuarios reales del cliente',
              'Registro de incidencias y criterio de severidad para habilitar el go-live',
              'Definición del criterio de go / no-go antes de la ventana de lanzamiento',
            ],
          },
          {
            title: 'Release y Cutover',
            items: [
              'Definición de la ventana de lanzamiento: fecha, hora y duración máxima',
              'Runbook de go-live: secuencia de pasos, responsables y validaciones',
              'Apagado del sistema anterior y migración de datos si aplica',
              'Smoke tests inmediatos post-lanzamiento',
              'Plan de rollback con condiciones de activación y tiempo máximo de ejecución',
              'Monitoreo intensivo durante las primeras 24 a 72 horas',
            ],
          },
          {
            title: 'Cierre formal',
            items: [
              'Acta de Cierre de Proyecto firmada por ambas partes',
              'Documento de Handover: qué se entrega, cómo funciona, cómo se opera y cómo se mantiene',
            ],
          },
        ],
        deliverable: 'Resultados de Testing / UAT documentados. Plan de Release y Cutover ejecutado. Acta de Cierre de Proyecto. Documento de Handover.',
        closeCriteria: 'La firma del Acta de Cierre y la aceptación del Handover por parte del cliente marcan la conclusión formal del proyecto y el inicio de la etapa de operación autónoma.',
      },
    ],
    ENG: [
      {
        num: '01',
        name: 'Kickoff',
        objective: 'Align every stakeholder on what will be built, how the team will work, and what success looks like for the project.',
        activities: [
          'Kickoff meeting with all stakeholders',
          'Kickoff Charter definition and signature',
          'Identification of required environments, owners, and availability dates',
          'Setup of the communication channel and progress meeting cadence',
        ],
        deliverable: 'Kickoff Charter signed by both parties.',
        closeCriteria: 'Both parties\' agreement on the Kickoff Charter enables the formal start of the project.',
      },
      {
        num: '02',
        name: 'Discovery',
        objective: 'Deeply understand the current state of the business and the processes the project will impact.',
        activities: [
          'Interviews with key users and the client\'s functional stakeholders',
          'Mapping of current processes',
          'Identification of improvement opportunities and gaps',
          'Assessment of existing systems, data sources, and integrations',
        ],
        subsections: [
          {
            title: 'AS-IS Document',
            text: 'Describes the current state of the business, the processes surveyed, and the problems identified. It\'s the foundation the solution is built on.',
          },
        ],
        deliverable: 'AS-IS Document approved by the client\'s functional stakeholder.',
        closeCriteria: 'The client functional stakeholder\'s approval of the AS-IS Document confirms that the discovery accurately reflects the business reality and enables progress toward the design phase. On greenfield projects, this phase focuses on documenting the context and objectives that will guide the solution.',
      },
      {
        num: '03',
        name: 'Design',
        objective: 'Precisely define what will be built and how, before writing a single line of code. This phase produces two complementary documents for different audiences.',
        subsections: [
          {
            title: 'Functional Specification — FSD',
            text: 'Describes what the system does in business language. The client can read and approve it without technical knowledge. Includes: executive summary, system actors, functional flows by module, business rules, included components with detailed descriptions, acceptance criteria per feature, and explicit scope exclusions.',
          },
          {
            title: 'Technical Design Document — TDD',
            text: 'Describes how the solution is implemented. Technical audience. Includes: solution architecture, technology stack with rationale, data model, integrations with external systems, branching strategy, CI/CD pipeline, security and performance considerations, and technical decisions with their trade-offs.',
          },
        ],
        deliverable: 'FSD approved by the client\'s functional stakeholder. TDD approved by the technical team.',
        closeCriteria: 'The client\'s approval of the FSD is the starting point of development. It represents mutual agreement on what will be built and is the reference that guides the technical team throughout implementation.',
      },
      {
        num: '04',
        name: 'Implementation',
        objective: 'Build the solution defined in the FSD and TDD, with continuous visibility for the client.',
        activities: [
          'Iterative development in two-week sprints by default, adjustable per project',
          'Periodic progress meetings with the client (weekly or biweekly)',
          'Client demo at the close of every sprint',
          'Continuous internal testing',
        ],
        deliverable: 'Progress Meeting Minutes for every periodic meeting. Deliverable Acceptance Record for every completed and validated milestone.',
        closeCriteria: 'Every completed milestone has its corresponding Acceptance Record approved, confirming that the built solution meets what was agreed in the FSD and enabling progress toward project closing.',
      },
      {
        num: '05',
        name: 'Closing & Go-Live',
        objective: 'Validate that the solution meets what was agreed, roll it out to production in a controlled way, and transfer knowledge to the client.',
        subsections: [
          {
            title: 'Testing & UAT',
            items: [
              'Execution of the Test Plan on the staging environment',
              'UAT with the client\'s real users',
              'Issue tracking and severity criteria to enable go-live',
              'Go / no-go criteria defined before the launch window',
            ],
          },
          {
            title: 'Release & Cutover',
            items: [
              'Launch window definition: date, time, and maximum duration',
              'Go-live runbook: step sequence, owners, and validations',
              'Legacy system shutdown and data migration if applicable',
              'Immediate post-launch smoke tests',
              'Rollback plan with activation conditions and maximum execution time',
              'Intensive monitoring during the first 24 to 72 hours',
            ],
          },
          {
            title: 'Formal closing',
            items: [
              'Project Closing Record signed by both parties',
              'Handover Document: what is delivered, how it works, how it is operated, and how it is maintained',
            ],
          },
        ],
        deliverable: 'Documented Testing / UAT results. Executed Release and Cutover Plan. Project Closing Record. Handover Document.',
        closeCriteria: 'Signing the Closing Record and the client\'s acceptance of the Handover mark the formal conclusion of the project and the start of the autonomous operation stage.',
      },
    ],
  };

  // ─── Documentos del proyecto ──────────────────────────────────────────────────
  private docsData: Record<'ESP' | 'ENG', DocRow[]> = {
    ESP: [
      { doc: 'Acta de Kickoff', phase: '1 — Inicio', approvedBy: 'Ambas partes' },
      { doc: 'Documento AS IS', phase: '2 — Relevamiento', approvedBy: 'Referente funcional cliente' },
      { doc: 'Especificación Funcional (FSD)', phase: '3 — Diseño', approvedBy: 'Referente funcional cliente' },
      { doc: 'Documento de Diseño Técnico (TDD)', phase: '3 — Diseño', approvedBy: 'Equipo técnico Patagonia Chain' },
      { doc: 'Acta de Reunión de Avance', phase: '4 — Implementación', approvedBy: 'Registro del proyecto' },
      { doc: 'Acta de Aceptación de Entregable', phase: '4 — Implementación', approvedBy: 'Ambas partes' },
      { doc: 'Solicitud de Cambio de Alcance', phase: 'Cuando aplica', approvedBy: 'Ambas partes' },
      { doc: 'Plan de Release y Cutover', phase: '5 — Cierre', approvedBy: 'Equipo técnico Patagonia Chain' },
      { doc: 'Acta de Cierre de Proyecto', phase: '5 — Cierre', approvedBy: 'Ambas partes' },
      { doc: 'Documento de Handover', phase: '5 — Cierre', approvedBy: 'Ambas partes' },
    ],
    ENG: [
      { doc: 'Kickoff Charter', phase: '1 — Kickoff', approvedBy: 'Both parties' },
      { doc: 'AS-IS Document', phase: '2 — Discovery', approvedBy: 'Client functional stakeholder' },
      { doc: 'Functional Specification (FSD)', phase: '3 — Design', approvedBy: 'Client functional stakeholder' },
      { doc: 'Technical Design Document (TDD)', phase: '3 — Design', approvedBy: 'Patagonia Chain technical team' },
      { doc: 'Progress Meeting Minutes', phase: '4 — Implementation', approvedBy: 'Project record' },
      { doc: 'Deliverable Acceptance Record', phase: '4 — Implementation', approvedBy: 'Both parties' },
      { doc: 'Scope Change Request', phase: 'When applicable', approvedBy: 'Both parties' },
      { doc: 'Release & Cutover Plan', phase: '5 — Closing', approvedBy: 'Patagonia Chain technical team' },
      { doc: 'Project Closing Record', phase: '5 — Closing', approvedBy: 'Both parties' },
      { doc: 'Handover Document', phase: '5 — Closing', approvedBy: 'Both parties' },
    ],
  };

  // ─── Comunicación y cadencia ──────────────────────────────────────────────────
  private cadenceData: Record<'ESP' | 'ENG', CadenceRow[]> = {
    ESP: [
      { label: 'Canal principal', value: 'Definido y acordado en el Kickoff' },
      { label: 'Reuniones de avance', value: 'Semanales o quincenales según el proyecto, siempre con Acta' },
      { label: 'Decisiones importantes', value: 'Siempre documentadas por escrito' },
      { label: 'Evolución del alcance', value: 'A través del proceso formal de Solicitud de Cambio' },
      { label: 'Reporte de estado', value: 'Incluido en cada Acta de Reunión de Avance' },
    ],
    ENG: [
      { label: 'Main channel', value: 'Defined and agreed on at Kickoff' },
      { label: 'Progress meetings', value: 'Weekly or biweekly depending on the project, always with Minutes' },
      { label: 'Important decisions', value: 'Always documented in writing' },
      { label: 'Scope evolution', value: 'Through the formal Scope Change Request process' },
      { label: 'Status reporting', value: 'Included in every Progress Meeting Minutes' },
    ],
  };

  principles: Principle[] = [];
  phases: Phase[] = [];
  docs: DocRow[] = [];
  cadence: CadenceRow[] = [];

  constructor(
    private router: Router,
    private title: Title,
    private meta: Meta,
  ) {}

  ngOnInit(): void {
    const savedLang = sessionStorage.getItem('idioma') as 'ESP' | 'ENG' | null;
    this.lang = savedLang ?? 'ESP';
    this.applyLang();

    this.title.setTitle('Metodología de Desarrollo de Software | Patagonia Chain Rosario');
    this.meta.updateTag({ name: 'description', content: 'Cómo trabaja Patagonia Chain, agencia de desarrollo de software en Rosario, Argentina: las 5 fases de todo proyecto, los entregables de cada etapa y cómo mantenemos la comunicación con el cliente.' });
    this.meta.updateTag({ property: 'og:title', content: 'Metodología de Desarrollo de Software | Patagonia Chain Rosario' });
    this.meta.updateTag({ property: 'og:description', content: 'Cómo trabaja Patagonia Chain, agencia de desarrollo de software en Rosario, Argentina: 5 fases, entregables claros y criterios de cierre en cada una.' });

    window.scroll(0, 0);
  }

  ngDoCheck(): void {
    try {
      const current = sessionStorage.getItem('idioma') as 'ESP' | 'ENG' | null;
      const next = current ?? 'ESP';
      if (next !== this.lang) {
        this.lang = next;
        this.applyLang();
      }
    } catch {}
  }

  private applyLang(): void {
    this.t = this.translations[this.lang];
    this.principles = this.principlesData[this.lang];
    this.phases = this.phasesData[this.lang];
    this.docs = this.docsData[this.lang];
    this.cadence = this.cadenceData[this.lang];
  }

  irr(s: string): void {
    if (s === 'home') this.router.navigate(['/']);
  }

  goHome(id: string): void {
    localStorage.setItem('pos', id);
    this.router.navigate(['/']);
  }

  ir(url: string): void {
    this.router.navigate(['/servicio/' + url]);
  }

  goMethod(): void {
    this.router.navigate(['/como-trabajamos']);
  }
}
